import { useRef, useState } from "react";
import { CheckCircle2, Loader2, Minus, Plus, Wallet } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/context/SiteContext";
import { http, apiError, formatINR, normalizeIndianPhone } from "@/lib/api";
import { getAttribution, getFbCookies } from "@/lib/tracking/attribution";
import { newEventId, trackAddToCart, trackPurchase } from "@/lib/tracking/tracker";
import { Field, Honeypot, PhoneInput, inputCls } from "./FormBits";
import { LeadForm } from "./LeadForm";

const shell = "max-h-[92dvh] w-[calc(100%-1.5rem)] overflow-y-auto overscroll-contain rounded-3xl border-0 bg-white p-6 sm:max-w-lg sm:rounded-3xl sm:p-8";

export const LeadModal = () => {
  const { modal, closeModal, content, t } = useSite();
  const open = modal.type === "lead";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeModal()}>
      <DialogContent data-testid="lead-modal" data-lenis-prevent className={shell}>
        <DialogTitle className="text-2xl font-extrabold leading-tight tracking-[-0.01em] text-forest sm:text-[28px]">{content.cta_text?.consult}</DialogTitle>
        <DialogDescription className="text-base leading-relaxed text-sage">
          {t("lead_modal_desc")}
        </DialogDescription>
        {open && <LeadForm ctaName={modal.cta} position={modal.position} startTracked idPrefix="modal-lead" />}
      </DialogContent>
    </Dialog>
  );
};

const PackStep = ({ packs, packId, setPackId, qty, setQty, max, onNext }) => {
  const { t } = useSite();
  const pack = packs.find((p) => p.id === packId) || packs[0];
  return (
    <div className="space-y-5">
      <div className="space-y-3" role="radiogroup" aria-label="Choose pack">
        {packs.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={p.id === pack.id} onClick={() => setPackId(p.id)} data-testid={`order-pack-${p.id}`}
            className={`flex w-full items-center justify-between rounded-2xl border-2 p-4 text-left transition-colors ${p.id === pack.id ? "border-forest bg-sand" : "border-forest/15"}`}>
            <span className="font-semibold text-forest">{p.label}</span>
            <span className="text-right">
              <span className="block text-xl font-extrabold text-forest">{formatINR(p.price)}</span>
              {p.mrp > p.price && <span className="text-sm text-sage line-through">{formatINR(p.mrp)}</span>}
            </span>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-forest">{t("quantity")}</span>
        <div className="flex items-center gap-1 rounded-full border border-forest/20 p-1">
          <button type="button" aria-label="Decrease quantity" data-testid="order-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="grid h-11 w-11 place-items-center rounded-full hover:bg-sand"><Minus className="h-4 w-4" /></button>
          <span data-testid="order-qty-value" className="w-8 text-center text-lg font-semibold">{qty}</span>
          <button type="button" aria-label="Increase quantity" data-testid="order-qty-plus" onClick={() => setQty(Math.min(max, qty + 1))} className="grid h-11 w-11 place-items-center rounded-full hover:bg-sand"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex items-center justify-between border-t border-forest/10 pt-4">
        <span className="text-sage">{t("total")}</span>
        <span data-testid="order-total" className="t-price text-forest">{formatINR(pack.price * qty)}</span>
      </div>
      <button type="button" onClick={() => onNext(pack)} data-testid="order-continue-button"
        className="min-h-[56px] w-full rounded-full bg-gold font-semibold text-forest transition-[filter,transform] hover:brightness-105 active:scale-[0.99]">
        {t("continue")}
      </button>
    </div>
  );
};

const validateOrder = (f, t) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = t("err_name");
  if (!normalizeIndianPhone(f.phone)) e.phone = t("err_phone");
  if (f.address.trim().length < 8) e.address = t("err_address");
  if (f.city.trim().length < 2) e.city = t("err_required");
  if (f.state.trim().length < 2) e.state = t("err_required");
  if (!/^[1-9]\d{5}$/.test(f.pincode)) e.pincode = t("err_pincode");
  return e;
};

const DetailsStep = ({ pack, qty, ctaName, position, onDone, onBack }) => {
  const { t } = useSite();
  const [f, setF] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "", website: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState("");
  const eventId = useRef(newEventId());
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const text = (k, label, props = {}) => (
    <Field label={label} id={`order-${k}`} error={errors[k]}>
      <input id={`order-${k}`} data-testid={`order-${k}-input`} value={f[k]} onChange={(e) => set(k)(e.target.value)} className={inputCls} {...props} />
    </Field>
  );
  const submit = async (ev) => {
    ev.preventDefault();
    if (busy) return;
    const e = validateOrder(f, t);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setServerError("");
    try {
      const { data } = await http.post("/orders", {
        ...f, phone: normalizeIndianPhone(f.phone), pack_id: pack.id, qty, payment_method: "cod", event_id: eventId.current,
        attribution: { ...getAttribution(), cta_name: ctaName, cta_position: position }, page_url: window.location.href, ...getFbCookies(),
      });
      if (!data.duplicate) trackPurchase({ orderId: data.order_id, eventId: eventId.current, value: data.value, qty });
      onDone({ ...data, name: f.name });
    } catch (err) {
      setServerError(apiError(err));
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate className="relative space-y-4" data-testid="order-form">
      <Honeypot value={f.website} onChange={set("website")} />
      {text("name", t("full_name"), { autoComplete: "name", maxLength: 80 })}
      <Field label={t("form_phone")} id="order-phone" error={errors.phone}>
        <PhoneInput id="order-phone" testId="order-phone-input" value={f.phone} onChange={set("phone")} />
      </Field>
      {text("address", t("address"), { autoComplete: "street-address", maxLength: 300 })}
      <div className="grid grid-cols-2 gap-3">
        {text("city", t("city"), { autoComplete: "address-level2" })}
        {text("state", t("state"), { autoComplete: "address-level1" })}
      </div>
      {text("pincode", t("pincode"), { inputMode: "numeric", maxLength: 6, autoComplete: "postal-code" })}
      <div className="flex items-center gap-3 rounded-2xl bg-sand p-4 text-sm text-forest">
        <Wallet className="h-5 w-5 shrink-0 text-leaf" aria-hidden />
        <span><strong>{t("cod_title")}</strong> · {t("cod_text", { amount: formatINR(pack.price * qty) })}</span>
      </div>
      {serverError && <p role="alert" data-testid="order-server-error" className="rounded-xl bg-red-50 p-3 text-sm font-medium text-red-700">{serverError}</p>}
      <button type="submit" disabled={busy} data-testid="order-submit-button"
        className="flex min-h-[56px] w-full items-center justify-center gap-2 rounded-full bg-forest font-semibold text-bone transition-[background-color] hover:bg-forest-deep disabled:opacity-70">
        {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}{busy ? t("placing") : t("place_order")}
      </button>
      <button type="button" onClick={onBack} data-testid="order-back-button" className="w-full py-2 text-sm font-semibold text-sage underline-offset-4 hover:underline">{t("back")}</button>
    </form>
  );
};

export const OrderModal = () => {
  const { modal, closeModal, content, t } = useSite();
  const open = modal.type === "order";
  const packs = content.packs?.length ? content.packs : [{ id: "single", label: "1 Jar", qty: 1, price: content.product.price, mrp: content.product.mrp }];
  const [step, setStep] = useState("pack");
  const [packId, setPackId] = useState(packs[0].id);
  const [qty, setQty] = useState(1);
  const [pack, setPack] = useState(null);
  const [done, setDone] = useState(null);

  const onOpenChange = (o) => {
    if (o) return;
    closeModal();
    setTimeout(() => { setStep("pack"); setDone(null); setQty(1); }, 250);
  };
  const next = (p) => { setPack(p); trackAddToCart(qty, p.price * qty); setStep("details"); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="order-modal" data-lenis-prevent className={shell}>
        <DialogTitle className="text-2xl font-extrabold leading-tight tracking-[-0.01em] text-forest sm:text-[28px]">
          {step === "done" ? t("order_received") : content.product.name}
        </DialogTitle>
        <DialogDescription className="text-base text-sage">
          {step === "pack" && `${content.product.pack_size} · ${t("order_choose")}`}
          {step === "details" && t("order_details_desc")}
          {step === "done" && t("order_thanks")}
        </DialogDescription>
        {step === "pack" && <PackStep packs={packs} packId={packId} setPackId={setPackId} qty={qty} setQty={setQty} max={content.product.max_qty || 5} onNext={next} />}
        {step === "details" && pack && <DetailsStep pack={pack} qty={qty} ctaName={modal.cta} position={modal.position} onBack={() => setStep("pack")} onDone={(d) => { setDone(d); setStep("done"); }} />}
        {step === "done" && done && (
          <div data-testid="order-success" className="rounded-2xl bg-sand p-7 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-leaf" aria-hidden />
            <p className="t-body mt-4 text-ink">{t("order_success", { name: done.name.split(" ")[0], amount: formatINR(done.value) })}</p>
            <p data-testid="order-id" className="mt-4 text-sm font-bold text-leaf">{t("order_id")}: {done.order_id}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export { LeadForm };
